import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import PaginationComponent from '../../../shared/components/PaginationComponent';
import { getInventoryMovements } from '../../services/api/implementation/impl';
import { formatINR, formatRateINR, movementTypeLabel } from '../../utils/inventoryValue';

const PAGE_SIZE = 20;
const qtyFmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const signed = (n) => {
  const value = Number(n || 0);
  if (!value) return '0';
  return `${value > 0 ? '+' : '−'}${qtyFmt(Math.abs(value))}`;
};

// The shared Modal takes a % width; go near full width on phones.
const modalWidth = () => (typeof window !== 'undefined' && window.innerWidth < 768 ? 96 : 70);

/**
 * The document a movement came from, read from its note ("PO #12 received",
 * "Lend #5 to …", "Return of lend #5", "Transfer #9 from …", "Material request #3").
 * Returns { label, to } where `to` is a page to open, or null.
 */
export function movementReference(movement) {
  const note = String(movement?.note || '');
  const lend = note.match(/lend #(\d+)/i);
  if (lend) return { label: `Lend #${lend[1]}`, to: '/builder/inventory/lends' };
  const transfer = note.match(/transfer #(\d+)/i);
  if (transfer) return { label: `Transfer #${transfer[1]}`, to: '/builder/inventory/transfers' };
  const po = note.match(/PO #(\d+)/i);
  if (po) return { label: `PO #${po[1]}`, to: `/builder/inventory/orders/items/${po[1]}` };
  const doc = note.match(/(Material request|Material return) #(\d+)/i);
  if (doc) return { label: `${doc[1]} #${doc[2]}`, to: null };
  if (movement?.task_id) return { label: `Task #${movement.task_id}`, to: null };
  return null;
}

/** Every stock change of one stock row (one item in one warehouse), newest first. */
const StockHistoryModal = ({ item, onClose }) => {
  const [page, setPage] = useState(1);
  const inventoryId = item?.inventory_id || item?.id;

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['inventoryMovements', inventoryId, page],
    queryFn: () => getInventoryMovements(inventoryId, { page, limit: PAGE_SIZE }),
    enabled: !!inventoryId,
    staleTime: 0
  });
  const rows = data?.items || [];
  const meta = data?.meta || {};
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const unit = meta.unit || item?.unit || '';
  const title = `Stock history: ${meta.item_name || item?.item_name || 'item'}${
    item?.warehouse_name ? ` (${item.warehouse_name})` : ''
  }`;

  return (
    <Modal name={title} width={modalWidth()} onClose={onClose}>
      <div className="mb-3 flex flex-wrap gap-4 text-sm text-gray-600">
        <span>
          In stock now:{' '}
          <span className="font-semibold text-gray-900">
            {qtyFmt(meta.physical_stock ?? item?.physicalStock)} {unit}
          </span>
        </span>
        <span>
          In transit:{' '}
          <span className="font-semibold text-gray-900">
            {qtyFmt(meta.in_transit_stock ?? item?.inTransitStock)} {unit}
          </span>
        </span>
        {meta.avg_rate !== undefined && (
          <span>
            Avg rate now:{' '}
            <span className="font-semibold text-gray-900">
              {formatRateINR(meta.avg_rate)}
              {meta.avg_rate != null && unit ? ` / ${unit}` : ''}
            </span>
            {meta.avg_rate == null && (
              <span className="ml-1 text-xs text-gray-400">(no rate yet)</span>
            )}
          </span>
        )}
      </div>

      {isError ? (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error?.response?.data?.message || error?.message || 'Could not load the history'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-100">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Qty</th>
                <th className="px-3 py-2">Stock after</th>
                <th className="px-3 py-2">Rate</th>
                <th className="px-3 py-2">Value</th>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">By</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                    {isFetching ? 'Loading…' : 'No stock changes recorded yet'}
                  </td>
                </tr>
              ) : (
                rows.map((m) => {
                  const qty = Number(m.quantity || 0);
                  const transit = Number(m.in_transit_quantity || 0);
                  const ref = movementReference(m);
                  const value = m.value == null ? null : Number(m.value);
                  return (
                    <tr key={m.id} className="border-t border-gray-100 align-top">
                      <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                        {m.created_at ? new Date(m.created_at).toLocaleString('en-IN') : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-900">{m.label || movementTypeLabel(m.type)}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {qty !== 0 && (
                          <span
                            className={`font-semibold ${
                              qty > 0 ? 'text-green-700' : 'text-red-600'
                            }`}>
                            {signed(qty)}
                          </span>
                        )}
                        {transit !== 0 && (
                          <span className="block text-xs text-gray-500">
                            {signed(transit)} in transit
                          </span>
                        )}
                        {qty === 0 && transit === 0 && <span className="text-gray-400">0</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {m.physical_after == null ? '—' : qtyFmt(m.physical_after)}
                        {transit !== 0 && m.in_transit_after != null && (
                          <span className="block text-xs text-gray-500">
                            {qtyFmt(m.in_transit_after)} in transit
                          </span>
                        )}
                      </td>
                      <td
                        className="whitespace-nowrap px-3 py-2 text-gray-700"
                        title="Average rate after this change">
                        {formatRateINR(m.avg_rate_after)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {value == null || value === 0 ? (
                          <span className="text-gray-400">{value === 0 ? '₹0' : '—'}</span>
                        ) : (
                          <span className={value > 0 ? 'text-green-700' : 'text-red-600'}>
                            {value > 0 ? '+' : ''}
                            {formatINR(value)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {ref ? (
                          ref.to ? (
                            <Link
                              to={ref.to}
                              className="font-medium text-[#2A565E] hover:underline">
                              {ref.label}
                            </Link>
                          ) : (
                            <span className="font-medium text-gray-800">{ref.label}</span>
                          )
                        ) : null}
                        {m.note && <span className="block text-xs text-gray-500">{m.note}</span>}
                        {!ref && !m.note && '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {m.created_by_name || (m.created_by ? `User #${m.created_by}` : 'System')}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-2">
          <PaginationComponent
            page={page}
            totalPages={totalPages}
            onPageChange={(p) => setPage(Math.min(Math.max(1, p), totalPages))}
            isFetching={isFetching}
          />
        </div>
      )}
    </Modal>
  );
};

export default StockHistoryModal;
